import { Component, Input } from '@angular/core';
import {
  categoryStructure,
  Classification,
  ClassificationReasonNotCovered,
  Contract,
  ContractPreview, InvoiceItemsCoverageCheckStatus,
  ParsedInvoiceData,
  PositionUI,
  ReasonNotCovered,
  Treatment,
  TreatmentUI
} from "../../../models";
import { MatDialog } from "@angular/material/dialog";
import {
  ContractClaimsListModalComponent
} from "../../contract/contract-claims-list-modal/contract-claims-list-modal.component";
import { DataStorageService } from "../../../services/data-storage.service";
import {
  checkIfTreatmentDateAfterContractEndDate,
  checkIfTreatmentDateBeforeContractStartDate
} from "../../../helpers";
import { SidebarDetailsService } from 'src/app/services/sidebar-details.service';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { ApiHttpService } from "../../../services/api-http.service";
import { TranslateService } from "@ngx-translate/core";
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'CPF-classification-list',
  templateUrl: './classification-list.component.html',
  styleUrls: ['./classification-list.component.scss']
})
export class ClassificationListComponent {
  treatments: TreatmentUI[] = [];
  contracts: Contract[] = [];
  expanded = true;

  public classificationType: any[] = []
  public classificationSubtype: any[] = []
  public classificationCause: any[] = []
  public classificationReasonNotCovered: { key: string, value: string }[] = [];

  constructor(private dialog: MatDialog, private dataService: DataStorageService, private sidebarDetailsService: SidebarDetailsService, private translate: TranslateService, private apiHttpService: ApiHttpService, private http: HttpClient) {
    this.classificationCause = this.dataService.classificationTypes.causes;
    this.classificationType = this.dataService.classificationTypes.types;
    this.classificationSubtype = this.dataService.classificationTypes.subTypes;

    this.translateReasons();

    this.translate.onLangChange.subscribe(() => {
      this.translateReasons();
    })
  }


  @Input() set treatmentsValue(parsedInvoiceData: ParsedInvoiceData) {
    this.treatments = parsedInvoiceData.treatments.map(t => (
        {
          ...t,
          expanded: false,
          comment: t.comments || [],
          positions: t.positions.map(p => {
              let classification = p.classification || {};

              if (parsedInvoiceData && parsedInvoiceData.recognizedVet && !parsedInvoiceData.recognizedVet.value && parsedInvoiceData.recognizedVet.needToSetReason) {
                classification.type = this.classificationType[0];
                classification.subtype = this.classificationSubtype[0];
                classification.cause = this.classificationCause[0];
                classification.covered = false;
                classification.reasonNotCovered = this.dataService.classificationTypes.reasonsNotCovered.get(ReasonNotCovered.NoCoverageAccordingToGTC);
              }

              if (t.contract && t.dateValidationStatus === InvoiceItemsCoverageCheckStatus.OPEN) {
                classification.covered = false;

                if (checkIfTreatmentDateBeforeContractStartDate(t.contract.start_date, t.date)) {
                  classification.reasonNotCovered = this.dataService.classificationTypes.reasonsNotCovered.get(ReasonNotCovered.DamageBeforeContractStart);
                } else if (checkIfTreatmentDateAfterContractEndDate(t.contract.end_date, t.date)) {
                  classification.reasonNotCovered = this.dataService.classificationTypes.reasonsNotCovered.get(ReasonNotCovered.DamageAfterContractEnd);
                }

                t.dateValidationStatus = InvoiceItemsCoverageCheckStatus.PERFORMED;
              }


              if (t.contract && t.contract.original && t.contract.original.trim() !== '') {
                classification.ignoreWaitingPeriod = true;
              } else {
                classification.ignoreWaitingPeriod = false;
              }

              return {
                ...p,
                copySource: false,
                copyTarget: false,
                classification: classification
              }
            }
          )
        }
      )
    );

    if (parsedInvoiceData && parsedInvoiceData.recognizedVet && parsedInvoiceData.recognizedVet.needToSetReason) {
      parsedInvoiceData.recognizedVet.needToSetReason = false;
    }
  }

  @Input() set contractSelected(contractSelected: ContractPreview[]) {
    this.contracts = contractSelected.map(c => c.contracts).flat();
    
    this.contracts.forEach((contract, index) => {
      console.log(`Contract ${index + 1}: ${contract.full_contract_number} - .original:`, contract.original);
    });
  }

  get treatmentsFilled() {
    return this.treatments.map(t => {
        const { expanded, ...tr } = t;

        //set booleans to false if they are undefined
        const treatment = {
          ...tr,
          positions: tr.positions.map(p => {
            let { copySource, copyTarget, classification, ...pos } = p;
            classification = classification || {};
            return {
              ...pos,
              classification: {
                ...classification,
                followUpTreatment: classification.followUpTreatment || false,
                preexistingCondition: classification.preexistingCondition || false,
                emergencyAbroad: classification.emergencyAbroad || false,
                chronicDisease: classification.chronicDisease || false,
                obligationsFulfilled: classification.obligationsFulfilled || false,
                covered: classification.covered || false,
              }
            }
          })
        }
        return treatment;
      }
    )
  }

  onAccidentDateChange(date: Date, p: any) {
    // Apply your custom logic to modify the date
    let tmp = this.toLocalIsoDateString(date)
    p.classification.accidentDate = tmp
  }

  translateReasons() {
    const reasonValues = Object.values([...this.dataService.classificationTypes.reasonsNotCovered.values()]);
    this.translate.get(reasonValues.map(value => `classification-types.reasons-not-covered.${value}`)).subscribe(
      (reasons: [string, string]) => {
        const returnArr: { key: string, value: string }[] = [];
        for (const key in reasons) {
          const value = reasons[key];
          const returnKey = key.split('.').at(-1) || 'fallback';
          const translated = value.indexOf('reasons-not-covered') === -1;
          if (translated) {
            returnArr.push({ key: returnKey, value: value });
          } else {
            returnArr.push({ key: returnKey, value: returnKey });
          }
        }

        this.classificationReasonNotCovered = returnArr;
      }
    )
  }

  typeChanged(position: PositionUI) {
    if (position.classification) {
      position.classification.subtype = undefined;
      position.classification.cause = undefined;
    }
  }

  subtypeChanged(position: PositionUI) {
    if (position.classification) {
      position.classification.cause = undefined;
    }
  }

  subtypesToShowForPosition(position: PositionUI) {
    if (!position.classification || !position.classification.type) {
      return [];
    }

    const type = position.classification.type;

    // @ts-ignore
    const availableSubtypes = categoryStructure[type];

    if (!availableSubtypes) {
      return [];
    }

    return Object.keys(availableSubtypes);
  }

  causeToShowForPosition(position: PositionUI) {
    if (!position.classification || !position.classification.type || !position.classification.subtype) {
      return [];
    }

    const type = position.classification.type;
    const subtype = position.classification.subtype;

    // @ts-ignore
    const availableCauses = categoryStructure[type][subtype];

    if (!availableCauses) {
      return [];
    }

    return availableCauses;
  }


  onFollowUpCheckboxChange(event: MatCheckboxChange, position: PositionUI) {
    if (!event.checked) {
      // Checkbox is unchecked
      if (position.classification) {
        position.classification.followUpTreatmentClaim = undefined
        position.classification.followUpTreatmentCluster = undefined
      }
    }
  }

  onCoveredCheckboxChange(event: MatCheckboxChange, position: PositionUI) {
    if (event.checked) {
      if (position.classification) {
        position.classification.reasonNotCovered = undefined;
        position.classification.reasonNotCoveredText = undefined;
      }
    }
  }

  async onReasonNotCoveredChange(reasonKey: string | undefined, position: PositionUI, treatment: TreatmentUI) {
    if (!position.classification || !treatment.contract) {
      return;
    }

    // Only auto-fill for specific reasons
    if (reasonKey === 'within-waiting-period' || reasonKey === 'damage-before-contract-start' || reasonKey === 'damage-after-contract-end') {
      const contractLanguage = treatment.contract.language?.toLowerCase() || 'de';
      const translatedText = await this.getReasonNotCoveredText(reasonKey, contractLanguage);
      
      if (translatedText) {
        position.classification.reasonNotCoveredText = translatedText;
      }
    } else {
      // Clear the text field if a different option is selected
      position.classification.reasonNotCoveredText = '';
    }
  }

  private async getReasonNotCoveredText(reasonKey: string, language: string): Promise<string> {
    try {
      const translations = await firstValueFrom(this.http.get(`/assets/i18n/${language}.json`));
      const translationKey = `classification-types.reasons-not-covered.${reasonKey}`;
      const translation = this.getNestedValue(translations, translationKey);
      return translation || '';
    } catch (error) {
      console.error(`Error loading translations for language ${language}:`, error);
      return '';
    }
  }

  // Helper method to get nested object value by dot notation
  private getNestedValue(obj: any, path: string): string {
    return path.split('.').reduce((current, key) => current?.[key], obj) || '';
  }

  toLocalIsoDateString(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  validate() {
    let valid = true;
    this.treatments.forEach(t => {

      if (t.contract) {
        t.positions.forEach(p => {
          if (!p.classification) {
            valid = false;
            return;
          }

          if (!(p.classification.type && p.classification.subtype && p.classification.cause && p.classification.clusterAssignment && p.classification.reasonForVisit && (p.classification.covered || (p.classification.reasonNotCovered && p.classification.reasonNotCoveredText)))) {
            valid = false;
          }

          if (this.askForAccidentDate(p)) {
            valid = !!p.classification.accidentDate;
          } else {
            // remove accident date if we do not need it
            p.classification.accidentDate = null
          }

        });
      }

    });
    return valid;
  }

  async openClaimsForContract(contract: Contract | null, classification: Partial<Classification>) {
    try {
      if (!contract) return;

      if (!contract.claims) {
        const claims = await this.apiHttpService.getClaimsForContract(contract.full_contract_number);
        contract.claims = claims;
      }

      this.dialog.open(ContractClaimsListModalComponent, {
        maxHeight: '90vh',
        width: '80vw',
        maxWidth: '1300px',
        data: {
          claims: contract.claims.filter(claimInfo => claimInfo.precedent_claim_id === "")
        }
      }).afterClosed().subscribe((claimNumber: string) => {
        delete contract.claims;
        classification.followUpTreatmentClaim = claimNumber;
      });
    } catch (e) {

    }
  }

  findAssignedContract(fullContractNumber: string | null) {
    return this.contracts.find(c => c.full_contract_number === fullContractNumber);
  }

  toggleExpandTreatment(treatment: TreatmentUI) {
    treatment.expanded = !treatment.expanded;
  }

  isSelectedModeActive(treatment: TreatmentUI) {
    return treatment.positions.some(p => p.copySource);
  }

  isApplyToOthersEnabled(treatment: TreatmentUI) {
    return this.treatments.every(t => t.positions.every(p => !p.copySource)) && treatment.positions.length > 1;
  }

  isSelectedAtLeastOne(treatment: TreatmentUI) {
    return treatment.positions.some(p => p.copyTarget);
  }

  applyToOthers(position: PositionUI) {
    position.copySource = true;
  }

  selectAll(treatment: TreatmentUI) {
    treatment.positions.filter(p => !p.copySource).forEach(p => p.copyTarget = true);
  }

  applySelection(treatment: TreatmentUI) {
    const source = treatment.positions.find(p => p.copySource);

    if (!source) return;

    treatment.positions.filter(p => p.copyTarget).forEach(p => {
      p.classification = { ...source.classification };
    });

    this.cancelSelection(treatment);
  }

  cancelSelection(treatment: TreatmentUI) {
    treatment.positions.forEach(p => {
      p.copySource = false;
      p.copyTarget = false;
    });
  }

  async openContractDetailsSidebar(contract: Contract) {
    if (!contract) {
      return;
    }

    await this.sidebarDetailsService.setContractDetails(contract);
    this.sidebarDetailsService.open();
  }

  askForAccidentDate(position: PositionUI): boolean {
    if (position.classification && position.classification.type) {
      return position.classification.type === 'unfall' && position.classification.followUpTreatment !== true
    }
    return false
  }
}
